/*
  <soeur-timeline> — reveals each step once as it enters the viewport and fills
  the connecting line to match.

  Two things worth knowing before changing this:

  1. The element adds `.soeur-timeline--ready` to itself before doing anything
     else. Every hidden-state rule in the CSS is scoped to that class, so if this
     script never loads the steps are simply visible. Do not move the hidden
     state out of that scope.

  2. The markup hooks are data attributes, not classes, so more than one section
     can share this behaviour. soeur-timeline (the Blueprint's process steps) and
     soeur-milestones (the shared history) both use it with quite different
     layouts. Anything with [data-timeline-step] inside a <soeur-timeline>
     reveals; add [data-timeline-rail] and [data-timeline-fill] as well and the
     connecting line fills to match. The ASAP page uses the reveal on its own.

  3. The fill is driven by the furthest step revealed so far, remembered as an
     index — not by the pixel height at reveal time. That is what lets a resize
     (or a breakpoint change, which reflows the whole layout) recompute the fill
     correctly instead of leaving it stuck at a stale height.
*/
class SoeurTimeline extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-timeline-step]'));
    this.fill = this.querySelector('[data-timeline-fill]');
    this.rail = this.querySelector('[data-timeline-rail]');
    // The line is optional. Sections that just want things to reveal as they
    // arrive (the ASAP page) supply steps and no rail or fill.
    if (!this.steps.length) return;

    this.furthest = -1;
    this.classList.add('soeur-timeline--ready');

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches || !('IntersectionObserver' in window)) {
      this.revealAll();
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      // ~30% of a step visible before it counts as arrived.
      threshold: 0.3,
      // Nothing counts until it is properly on screen rather than clipped at
      // the very bottom edge.
      rootMargin: '0px 0px -10% 0px'
    });
    this.steps.forEach((step) => this.observer.observe(step));

    this.onResize = this.onResize.bind(this);
    window.addEventListener('resize', this.onResize, { passive: true });

    // If the user switches reduced motion on mid-visit, stop animating.
    this.onMotionChange = () => { if (reduced.matches) this.revealAll(); };
    reduced.addEventListener('change', this.onMotionChange);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const step = entry.target;
      step.classList.add('is-revealed');
      // One-shot: stop watching once revealed, so scrolling back up leaves it be.
      this.observer.unobserve(step);
      const index = this.steps.indexOf(step);
      if (index > this.furthest) {
        this.furthest = index;
        this.updateFill();
      }
    });
  }

  revealAll() {
    this.steps.forEach((step) => step.classList.add('is-revealed'));
    this.furthest = this.steps.length - 1;
    if (this.observer) this.observer.disconnect();
    this.updateFill();
  }

  onResize() {
    // Layout changes on resize — most sharply when crossing the breakpoint
    // where the line moves from centre to left — so recompute from the
    // remembered index rather than trusting the old height.
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(() => this.updateFill());
  }

  updateFill() {
    if (!this.fill || !this.rail) return;
    if (this.furthest < 0) {
      this.fill.style.setProperty('--soeur-timeline-fill', '0%');
      return;
    }
    // Fill to the vertical middle of the furthest revealed step, so the line
    // reads as "we are here" rather than racing ahead of the content.
    const step = this.steps[this.furthest];
    const railHeight = this.rail.offsetHeight;
    if (!railHeight) return;
    const centre = step.offsetTop + step.offsetHeight / 2;
    const last = this.furthest === this.steps.length - 1;
    const percent = last ? 100 : Math.min(100, (centre / railHeight) * 100);
    this.fill.style.setProperty('--soeur-timeline-fill', percent.toFixed(2) + '%');
  }
}

if (!customElements.get('soeur-timeline')) {
  customElements.define('soeur-timeline', SoeurTimeline);
}
